import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE, fmtUSD, fmtPct } from "@/lib/api";
import { PageHeader, Card } from "@/components/Bits";
import { ArrowLeft, Upload, Trash2, X, Plus, ImageIcon, Save, Video } from "lucide-react";
import { toast } from "sonner";
import VideoPlayer from "@/components/VideoPlayer";

const ScreenshotImg = ({ id }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    api.get(`/screenshots/${id}/file`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); setUrl(u); revoked = u; })
      .catch(() => {});
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [id]);
  if (!url) return <div className="w-full h-40 bg-[color:var(--surface)] animate-pulse" />;
  return <img src={url} alt="trade screenshot" className="w-full h-40 object-cover hover:opacity-80 transition-opacity cursor-pointer" />;
};

export default function TradeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [notes, setNotes] = useState("");
  const [setupVal, setSetupVal] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: trade, isLoading } = useQuery({
    queryKey: ["trade", id],
    queryFn: async () => (await api.get(`/trades/${id}`)).data,
  });

  const { data: shots = [] } = useQuery({
    queryKey: ["shots", id],
    queryFn: async () => (await api.get(`/trades/${id}/screenshots`)).data,
  });

  const { data: videoClips = [] } = useQuery({
    queryKey: ["video-clips", "trade", id],
    queryFn: async () => (await api.get("/video/clips", { params: { trade_id: id } })).data,
  });

  /* eslint-disable */
  useEffect(() => {
    if (trade) {
      setNotes(trade.notes || "");
      setSetupVal(trade.setup || "");
      setTags(trade.tags || []);
    }
  }, [trade]);
  /* eslint-enable */

  if (isLoading) return <div className="p-8 text-xs font-mono-num text-[color:var(--fg-3)]">LOADING…</div>;
  if (!trade) return <div className="p-8 text-xs font-mono-num text-rose-400">TRADE NOT FOUND</div>;

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/trades/${id}`, { notes, tags, setup: setupVal });
      toast.success("TRADE UPDATED");
      qc.invalidateQueries({ queryKey: ["trade", id] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["taxonomy"] });
    } catch (e) {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/trades/${id}/screenshots`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SCREENSHOT UPLOADED");
      qc.invalidateQueries({ queryKey: ["shots", id] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteShot = async (sid) => {
    try {
      await api.delete(`/screenshots/${sid}`);
      qc.invalidateQueries({ queryKey: ["shots", id] });
    } catch { toast.error("Failed to delete"); }
  };

  const winColor = trade.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]";

  return (
    <div>
      <PageHeader
        eyebrow={`TRADE ${trade.id.slice(0, 8).toUpperCase()}`}
        title={`${trade.symbol} · ${trade.side.toUpperCase()}`}
        action={
          <button onClick={() => navigate("/trades")} className="flex items-center gap-2 px-3 py-2 text-xs bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)] transition-colors" data-testid="back-to-trades">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> BACK
          </button>
        }
      />

      <div className="p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* P&L Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
            <div className="p-4">
              <div className="label-eyebrow">P&L</div>
              <div className={`font-mono-num text-xl mt-1 ${winColor}`}>{fmtUSD(trade.pnl)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">P&L %</div>
              <div className={`font-mono-num text-xl mt-1 ${winColor}`}>{fmtPct(trade.pnl_pct)}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">QTY</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]">{trade.quantity}</div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow">HOLD</div>
              <div className="font-mono-num text-xl mt-1 text-[color:var(--fg)]">{trade.hold_minutes.toFixed(0)}m</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-b [&>*]:border-[color:var(--border)] [&>*:nth-child(2n)]:border-r-0">
            <div className="p-4"><div className="label-eyebrow">ENTRY</div><div className="font-mono-num mt-1">{trade.entry_price.toFixed(2)} @ {trade.entry_time.slice(0,16).replace("T"," ")}</div></div>
            <div className="p-4"><div className="label-eyebrow">EXIT</div><div className="font-mono-num mt-1">{trade.exit_price.toFixed(2)} @ {trade.exit_time.slice(0,16).replace("T"," ")}</div></div>
            <div className="p-4"><div className="label-eyebrow">FEES</div><div className="font-mono-num mt-1">{fmtUSD(trade.fees)}</div></div>
            <div className="p-4"><div className="label-eyebrow">CREATED</div><div className="font-mono-num mt-1 text-[color:var(--fg-3)]">{trade.created_at?.slice(0,16).replace("T"," ")}</div></div>
          </div>

          {/* Screenshots */}
          <Card eyebrow="EVIDENCE" title="Screenshots" action={
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="screenshot-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-screenshot-btn" className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors disabled:opacity-50">
                <Upload className="h-3 w-3" strokeWidth={1.5} /> {uploading ? "UPLOADING…" : "ADD"}
              </button>
            </>
          }>
            {shots.length === 0 ? (
              <div className="text-center py-10 text-[color:var(--fg-3)]">
                <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
                <div className="text-xs label-eyebrow">NO SCREENSHOTS YET</div>
                <div className="text-xs mt-2">Drop a chart screenshot to remember exactly what you saw.</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {shots.map((s) => (
                  <div key={s.id} className="relative group border border-[color:var(--border)]" data-testid={`shot-${s.id}`}>
                    <ScreenshotImg id={s.id} />
                    <button
                      onClick={() => deleteShot(s.id)}
                      className="absolute top-1 right-1 p-1 bg-[color:var(--bg)]/80 text-[color:var(--fg-2)] hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-shot-${s.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {videoClips.length > 0 && (
            <Card eyebrow="VIDEO REPLAY" title={videoClips.length === 1 ? "Trade replay" : `${videoClips.length} replays`}>
              <div className="space-y-4">
                {videoClips.map((c) => (
                  <div key={c.id} data-testid={`clip-${c.id}`}>
                    <VideoPlayer
                      clip={c}
                      streamUrl={`${API_BASE}/video/clips/${c.id}/stream?token=${encodeURIComponent(localStorage.getItem("edge_token") || "")}`}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right column: editable annotations */}
        <div className="space-y-6">
          <Card eyebrow="ANNOTATIONS" title="Notes, setup & tags" action={
            <button onClick={save} disabled={saving} data-testid="save-trade-btn" className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white text-black hover:bg-[color:var(--accent)] transition-colors disabled:opacity-50">
              <Save className="h-3 w-3" strokeWidth={1.5} /> {saving ? "SAVING…" : "SAVE"}
            </button>
          }>
            <div className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">SETUP</label>
                <input
                  type="text" value={setupVal} onChange={(e) => setSetupVal(e.target.value)}
                  data-testid="setup-input"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)]"
                  placeholder="e.g. breakout, fade, trend-pullback"
                />
              </div>

              <div>
                <label className="label-eyebrow block mb-1.5">TAGS</label>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="tag-chips">
                  {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[rgba(34,197,94,0.10)] text-[color:var(--accent)] border border-[rgba(34,197,94,0.30)] font-mono-num">
                      {t}
                      <button onClick={() => removeTag(t)} className="hover:text-rose-400" data-testid={`remove-tag-${t}`}><X className="h-3 w-3" strokeWidth={1.5} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    placeholder="e.g. fomo, plan, A+, news"
                    data-testid="tag-input"
                    className="flex-1 bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-xs focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                  />
                  <button onClick={addTag} data-testid="add-tag-btn" className="px-2 bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)] text-xs">
                    <Plus className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div>
                <label className="label-eyebrow block mb-1.5">NOTES</label>
                <textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={8}
                  data-testid="notes-input"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)] resize-none"
                  placeholder="What was the thesis? What did you see? What would you do differently?"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
